import React, { useState, useMemo } from 'react';
import { RawTrader, TraderDerivation } from './types';
import {
  PERIODS,
  SCORE_META,
  RADAR_LABELS,
  MONTH_LABELS,
  series,
  monthsCalc,
  chipStyle,
  money,
  sMoney,
  sPct,
  POS,
  NEG,
  INK,
  TEAL,
} from './constants';

interface OverviewTabProps {
  trader: RawTrader;
  derivation: TraderDerivation;
  riskTone: { fg: string; bg: string; bd: string };
  riskLabel: string;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  trader,
  derivation: d,
  riskTone,
  riskLabel,
}) => {
  const [period, setPeriod] = useState<string>('3M');
  const [scoreOpen, setScoreOpen] = useState<boolean>(false);

  const s = useMemo(() => series(trader), [trader]);

  const cut = useMemo(() => {
    const map: Record<string, number> = {
      '1M': 22,
      '3M': 65,
      '6M': 100,
      YTD: 118,
      'Tất cả': s.length,
    };
    return map[period] || 65;
  }, [period, s.length]);

  const vis = useMemo(() => s.slice(Math.max(0, s.length - cut)), [s, cut]);
  const lo = useMemo(() => Math.min(...vis, 0), [vis]);
  const hi = useMemo(() => Math.max(...vis, 0.1), [vis]);

  const X = (k: number) => (vis.length > 1 ? ((k / (vis.length - 1)) * 1000).toFixed(1) : '0');
  const Y = (v: number) => (290 - ((v - lo) / (hi - lo)) * 280).toFixed(1);
  const line = useMemo(() => vis.map((v, k) => X(k) + ',' + Y(v)).join(' '), [vis, lo, hi]);

  const curve = useMemo(() => {
    return {
      line,
      area: `0,290 ${line} 1000,290`,
      eq: vis.map((v, k) => X(k) + ',' + Y(v * 0.97 - 0.15)).join(' '),
      labels: period === '1M' ? ['4 tuần trước', '3 tuần', '2 tuần', 'Tuần này'] : ['T5', 'T6', 'T7', 'T8', 'T9'],
    };
  }, [line, vis, period, lo, hi]);

  const radarVals = useMemo(() => {
    return [
      trader.scores[0],
      trader.scores[1],
      trader.scores[2],
      trader.scores[3],
      trader.wr * 1.35,
      Math.min(d.perWeek * 9, 100),
    ];
  }, [trader, d.perWeek]);

  const pt = (i: number, r: number) => {
    const a = -Math.PI / 2 + (i * Math.PI) / 3;
    return [130 + Math.cos(a) * r, 110 + Math.sin(a) * r];
  };
  const poly = (r: number) =>
    Array.from({ length: 6 }, (_, i) => pt(i, r).map((n) => n.toFixed(1)).join(',')).join(' ');

  const radar = useMemo(() => {
    return {
      rings: [30, 55, 80].map(poly),
      spokes: Array.from({ length: 6 }, (_, i) => {
        const [x, y] = pt(i, 80);
        return { x: x.toFixed(1), y: y.toFixed(1) };
      }),
      shape: radarVals
        .map((v, i) => pt(i, (Math.max(v, 8) / 100) * 80).map((n) => n.toFixed(1)).join(','))
        .join(' '),
      dots: radarVals.map((v, i) => {
        const [x, y] = pt(i, (Math.max(v, 8) / 100) * 80);
        return { x: x.toFixed(1), y: y.toFixed(1) };
      }),
      labels: RADAR_LABELS.map((name, i) => {
        const [x, y] = pt(i, 99);
        return {
          name,
          val: String(Math.round(radarVals[i])),
          left: ((x / 260) * 100).toFixed(1) + '%',
          top: ((y / 220) * 100).toFixed(1) + '%',
        };
      }),
    };
  }, [radarVals]);

  const mv = useMemo(() => monthsCalc(trader), [trader]);
  const maxAbs = useMemo(() => Math.max(...mv.map(Math.abs), 0.5), [mv]);
  const months = useMemo(() => {
    return mv.map((v, i) => ({
      label: MONTH_LABELS[i],
      v: sPct(v),
      c: v >= 0 ? POS : NEG,
      posH: (v > 0 ? Math.round((v / maxAbs) * 82) : 0) + 'px',
      negH: (v < 0 ? Math.round((-v / maxAbs) * 40) : 0) + 'px',
    }));
  }, [mv, maxAbs]);

  const winMonths = useMemo(() => mv.filter((v) => v > 0).length, [mv]);

  const symBase = trader.tags.includes('Indices')
    ? ['XAUUSD', 'EURUSD', 'US30', 'GBPUSD']
    : ['XAUUSD', 'EURUSD', 'GBPUSD', 'USDJPY'];
  const shares = [42, 27, 19, 12];
  const symbols = symBase.map((sym, i) => ({
    sym,
    share: shares[i] + '%',
    trades: Math.round((d.trades * shares[i]) / 100),
  }));

  const scoreRows = SCORE_META.map(([k, w, n], i) => ({
    k,
    w,
    n,
    v: trader.scores[i],
    pct: trader.scores[i] + '%',
    c: trader.scores[i] >= 85 ? '#1FA463' : trader.scores[i] >= 65 ? '#E39A2B' : '#E0524D',
  }));

  const perfCells = [
    { k: 'Lợi nhuận tổng', v: sMoney(d.profit), c: d.profit >= 0 ? POS : NEG, n: 'trên ' + money(trader.cap) + ' vốn' },
    { k: 'Tăng trưởng', v: sPct(trader.ret), c: trader.ret >= 0 ? POS : NEG, n: trader.days + ' ngày giao dịch' },
    { k: 'Win rate', v: trader.wr + '%', c: INK, n: d.wins + '/' + d.trades + ' lệnh' },
    { k: 'Profit factor', v: trader.pf.toFixed(2), c: trader.pf >= 1.3 ? POS : trader.pf >= 1 ? INK : NEG, n: 'lãi gộp / lỗ gộp' },
    { k: 'Lệnh trung bình', v: sMoney(d.exp), c: d.exp >= 0 ? POS : NEG, n: 'expected payoff' },
    { k: 'Risk / reward', v: '1 : ' + d.rr.toFixed(2), c: INK, n: 'trung bình mỗi lệnh' },
  ];

  const riskCells = [
    { k: 'Max drawdown', v: trader.dd.toFixed(1) + '%', c: trader.dd > 10 ? NEG : INK, n: money((trader.cap * trader.dd) / 100) },
    { k: 'Drawdown hiện tại', v: trader.curDd.toFixed(1) + '%', c: trader.curDd > 8 ? NEG : INK, n: trader.curDd > 8 ? 'gần mức sâu nhất' : 'trong vùng an toàn' },
    { k: 'Rủi ro / lệnh', v: d.avgRisk.toFixed(2) + '%', c: d.avgRisk > 1 ? NEG : INK, n: 'trung bình 90 ngày' },
    { k: 'Vi phạm giới hạn', v: String(trader.viol), c: trader.viol === 0 ? POS : NEG, n: '90 ngày gần nhất' },
    { k: 'Recovery factor', v: d.recovery.toFixed(2), c: d.recovery >= 1.5 ? POS : INK, n: 'lợi nhuận / drawdown' },
    { k: 'Risk score', v: trader.risk + '/100', c: trader.risk >= 85 ? POS : trader.risk >= 65 ? '#A06C10' : NEG, n: 'điểm kiểm soát rủi ro' },
  ];

  const consCells = [
    { k: 'Ngày giao dịch', v: String(trader.days), c: INK },
    { k: 'Lệnh / tuần', v: String(d.perWeek), c: INK },
    { k: 'Thắng liên tiếp', v: String(d.cWins), c: POS },
    { k: 'Thua liên tiếp', v: String(d.cLosses), c: NEG },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Top Section: Growth Chart + Radar Chart */}
      <div className="flex flex-wrap gap-4 items-stretch">
        {/* Growth Chart */}
        <div className="flex-[2] min-w-[320px] card-box p-4 md:p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3.5">
            <div>
              <div className="text-xs text-[#8B95A3]">Tăng trưởng từ {trader.since}</div>
              <div className="flex items-baseline gap-2.5 mt-1">
                <span
                  style={{ color: trader.ret >= 0 ? POS : NEG }}
                  className="text-2xl md:text-3xl font-bold"
                >
                  {sPct(trader.ret)}
                </span>
                <span className="font-mono text-sm text-[#8B95A3]">{sMoney(d.profit)}</span>
              </div>
            </div>
            <div className="flex gap-1">
              {PERIODS.map((label) => {
                const active = period === label;
                const cStyle = chipStyle(active);
                return (
                  <button
                    key={label}
                    onClick={() => setPeriod(label)}
                    style={cStyle}
                    className="cursor-pointer py-1.5 px-3 text-xs font-semibold !rounded-[4px] border outline-none"
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="relative w-full h-[220px]">
            <svg viewBox="0 0 1000 300" preserveAspectRatio="none" className="w-full h-full block">
              <line x1="0" y1="75" x2="1000" y2="75" stroke="#F0F3F7" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              <line x1="0" y1="150" x2="1000" y2="150" stroke="#F0F3F7" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              <line x1="0" y1="225" x2="1000" y2="225" stroke="#F0F3F7" strokeWidth="1" vectorEffect="non-scaling-stroke" />
              <polygon points={curve.area} fill="rgba(53,208,206,0.12)" />
              <polyline points={curve.line} fill="none" stroke="#0E9C9C" strokeWidth="2" vectorEffect="non-scaling-stroke" />
              <polyline points={curve.eq} fill="none" stroke="#E39A2B" strokeWidth="1.3" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
            </svg>
          </div>

          <div className="flex justify-between mt-2 font-mono text-xs text-[#A6AFBC]">
            {curve.labels.map((lb) => (
              <div key={lb}>{lb}</div>
            ))}
          </div>

          <div className="flex gap-4 mt-3 text-xs text-[#5C6779]">
            <div className="flex items-center gap-1.5">
              <div className="w-3.5 h-0.5 bg-[#0E9C9C]"></div>Balance
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3.5 h-0 border-t-[1.3px] border-dashed border-[#E39A2B]"></div>Equity
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-4 pt-4 border-t border-[#EEF1F5]">
            <div>
              <div className="text-xs text-[#8B95A3] mb-1">Balance</div>
              <div className="font-mono text-sm font-semibold text-gray-900">{money(d.balance)}</div>
            </div>
            <div>
              <div className="text-xs text-[#8B95A3] mb-1">Equity</div>
              <div className="font-mono text-sm font-semibold text-gray-900">{money(d.equity)}</div>
            </div>
            <div>
              <div className="text-xs text-[#8B95A3] mb-1">Vốn ban đầu</div>
              <div className="font-mono text-sm font-semibold text-gray-900">{money(trader.cap)}</div>
            </div>
            <div>
              <div className="text-xs text-[#8B95A3] mb-1">Drawdown hiện tại</div>
              <div
                style={{ color: trader.curDd > 8 ? NEG : INK }}
                className="font-mono text-sm font-semibold"
              >
                {trader.curDd.toFixed(1)}%
              </div>
            </div>
          </div>
        </div>

        {/* Trader Score & Radar */}
        <div className="flex-1 min-w-[280px] card-box p-4 md:p-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-gray-900">Trader Score</div>
              <div className="text-xs text-[#8B95A3] mt-0.5">
                Top {trader.score >= 90 ? '5' : trader.score >= 80 ? '20' : trader.score >= 70 ? '40' : '60'}% hệ thống
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-3xl font-semibold text-[#A06C10] leading-none">
                {trader.score}
              </div>
              <div className="text-[10px] text-[#B9A183] font-semibold mt-1">/ 100</div>
            </div>
          </div>

          <div className="relative w-full max-w-[280px] aspect-[260/220] mx-auto my-3">
            <svg viewBox="0 0 260 220" className="w-full h-full block">
              {radar.rings.map((ringPoints, idx) => (
                <polygon key={idx} points={ringPoints} fill="none" stroke="#E7EBF0" strokeWidth="1" />
              ))}
              {radar.spokes.map((spoke, idx) => (
                <line key={idx} x1="130" y1="110" x2={spoke.x} y2={spoke.y} stroke="#EEF1F5" strokeWidth="1" />
              ))}
              <polygon points={radar.shape} fill="rgba(53,208,206,0.18)" stroke="#0E9C9C" strokeWidth="1.6" />
              {radar.dots.map((dot, idx) => (
                <circle key={idx} cx={dot.x} cy={dot.y} r="2.6" fill="#0E9C9C" />
              ))}
            </svg>
            {radar.labels.map((lbl, idx) => (
              <div
                key={idx}
                style={{ left: lbl.left, top: lbl.top }}
                className="absolute -translate-x-1/2 -translate-y-1/2 w-20 text-center text-[9.5px] font-medium text-[#5C6779] leading-tight"
              >
                {lbl.name}
                <span className="block font-mono font-semibold text-[#16202E]">{lbl.val}</span>
              </div>
            ))}
          </div>

          <div
            onClick={() => setScoreOpen(!scoreOpen)}
            className="cursor-pointer flex items-center justify-between gap-2.5 mt-3 pt-3 border-t border-[#EEF1F5] hover:opacity-80"
          >
            <span className="text-xs font-semibold text-[#0E9C9C]">
              {scoreOpen ? 'Thu gọn chi tiết điểm' : 'Xem chi tiết 5 tiêu chí'}
            </span>
            <span
              style={{ transform: scoreOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
              className="text-xs text-[#0E9C9C] transition-transform"
            >
              ▾
            </span>
          </div>

          {scoreOpen && (
            <div className="flex flex-col gap-2.5 pt-3">
              {scoreRows.map((r) => (
                <div key={r.k}>
                  <div className="flex justify-between items-baseline mb-1">
                    <div className="text-xs font-semibold text-gray-800">
                      {r.k}
                      <span className="text-[#A6AFBC] font-normal"> · {r.w}</span>
                    </div>
                    <div className="font-mono text-xs font-semibold text-gray-900">{r.v}</div>
                  </div>
                  <div className="h-1.5 bg-[#F0F3F7] !rounded-full overflow-hidden">
                    <div style={{ width: r.pct, background: r.c }} className="h-full !rounded-full"></div>
                  </div>
                </div>
              ))}
              <div className="text-[11px] text-[#A6AFBC] mt-2">
                Điểm có điều chỉnh theo rủi ro: lợi nhuận cao đi kèm rủi ro cao không làm tăng điểm.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Grid 6 Detailed Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
        {/* Card 1: Hiệu suất chính */}
        <div className="card-box p-4 md:p-5">
          <div className="text-sm font-bold text-gray-900 mb-0.5">Hiệu suất chính</div>
          <div className="text-xs text-[#8B95A3] mb-4">Trader kiếm được bao nhiêu và bằng cách nào</div>
          <div className="grid grid-cols-2 gap-2.5">
            {perfCells.map((c) => (
              <div key={c.k} className="bg-[#F7F9FB] !rounded-lg p-3">
                <div className="text-[11px] text-[#5C6779] mb-1">{c.k}</div>
                <div style={{ color: c.c }} className="font-mono text-sm font-semibold">
                  {c.v}
                </div>
                <div className="text-[10px] text-[#A6AFBC] mt-0.5">{c.n}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Card 2: Rủi ro */}
        <div className="card-box p-4 md:p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="text-sm font-bold text-gray-900 mb-0.5">Rủi ro</div>
              <div className="text-xs text-[#8B95A3]">Mức rủi ro để tạo ra lợi nhuận trên</div>
            </div>
            <span
              style={{ background: riskTone.bg, color: riskTone.fg }}
              className="text-xs font-bold py-1 px-2.5 !rounded-md whitespace-nowrap"
            >
              RỦI RO {riskLabel.toUpperCase()}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            {riskCells.map((c) => (
              <div key={c.k} className="bg-[#F7F9FB] !rounded-lg p-3">
                <div className="text-[11px] text-[#5C6779] mb-1">{c.k}</div>
                <div style={{ color: c.c }} className="font-mono text-sm font-semibold">
                  {c.v}
                </div>
                <div className="text-[10px] text-[#A6AFBC] mt-0.5">{c.n}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Card 3: Tính ổn định */}
        <div className="card-box p-4 md:p-5">
          <div className="text-sm font-bold text-gray-900 mb-0.5">Tính ổn định</div>
          <div className="text-xs text-[#8B95A3] mb-4">
            {winMonths} tháng dương / {12 - winMonths} tháng âm trong 12 tháng
          </div>
          <div className="flex items-stretch gap-1 h-[130px]">
            {months.map((m) => (
              <div key={m.label} className="flex-1 flex flex-col justify-end">
                <div style={{ height: m.posH }} className="bg-[#1FA463] !rounded-t-sm"></div>
                <div className="h-px bg-[#E7EBF0]"></div>
                <div style={{ height: m.negH }} className="bg-[#E0524D] !rounded-b-sm"></div>
                <div className="font-mono text-[9px] text-center text-[#A6AFBC] mt-1.5">{m.label}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2.5 mt-4 pt-4 border-t border-[#EEF1F5]">
            {consCells.map((c) => (
              <div key={c.k}>
                <div className="text-xs text-[#8B95A3] mb-1">{c.k}</div>
                <div style={{ color: c.c }} className="font-mono text-sm font-semibold">
                  {c.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Card 4: Tài khoản theo stage */}
        <div className="card-box p-4 md:p-5">
          <div className="text-sm font-bold text-gray-900 mb-0.5">Tài khoản theo stage</div>
          <div className="text-xs text-[#8B95A3] mb-4">Demo hiển thị riêng, không tính vào số liệu tổng hợp</div>
          <div className="flex flex-col gap-2">
            {trader.accounts.map(([accStage, detail, capVal, retVal]) => {
              const isDemo = accStage === 'Demo';
              const barColor = accStage === 'Funded' ? '#E39A2B' : isDemo ? '#C6CDD6' : TEAL;
              return (
                <div
                  key={accStage}
                  className={`flex items-center gap-3 p-3 !rounded-lg ${isDemo ? 'bg-[#FAFBFD]' : 'bg-[#F7F9FB]'
                    }`}
                >
                  <div style={{ background: barColor }} className="w-1 h-7 !rounded-sm flex-shrink-0"></div>
                  <div className="flex-1 min-w-0">
                    <div
                      style={{ color: isDemo ? '#8B95A3' : INK }}
                      className="text-xs font-bold truncate"
                    >
                      {accStage}
                    </div>
                    <div className="text-[11px] text-[#8B95A3] mt-0.5 truncate">{detail}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="font-mono text-xs font-semibold text-gray-900">{money(capVal)}</div>
                    <div
                      style={{ color: retVal >= 0 ? POS : NEG }}
                      className="font-mono text-[11px] mt-0.5"
                    >
                      {sPct(retVal)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Card 5: Phong cách giao dịch */}
        <div className="card-box p-4 md:p-5">
          <div className="text-sm font-bold text-gray-900 mb-0.5">Phong cách giao dịch</div>
          <div className="text-xs text-[#8B95A3] mb-4">Trader này giao dịch cái gì và theo hướng nào</div>
          <div className="flex justify-between text-xs font-semibold mb-1.5">
            <span className="text-[#0E9C9C]">Long {trader.long}%</span>
            <span className="text-[#A06C10]">Short {100 - trader.long}%</span>
          </div>
          <div className="flex h-2 !rounded-full overflow-hidden mb-4">
            <div style={{ width: trader.long + '%' }} className="bg-[#0E9C9C]"></div>
            <div className="flex-1 bg-[#E39A2B]"></div>
          </div>
          <div className="flex flex-col gap-2.5">
            {symbols.map((sItem) => (
              <div key={sItem.sym}>
                <div className="flex justify-between font-mono text-xs mb-1">
                  <span className="font-semibold text-gray-900">{sItem.sym}</span>
                  <span className="text-[#8B95A3]">
                    {sItem.share} · {sItem.trades} lệnh
                  </span>
                </div>
                <div className="h-1 bg-[#F0F3F7] !rounded-full overflow-hidden">
                  <div style={{ width: sItem.share }} className="h-full bg-[#06223B] !rounded-full"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Card 6: Đánh giá từ Mentor */}
        <div className="card-box p-4 md:p-5">
          <div className="flex justify-between items-start gap-3 mb-3.5">
            <div>
              <div className="text-sm font-bold text-gray-900 mb-0.5">Đánh giá từ Mentor</div>
              <div className="text-xs text-[#8B95A3]">Công khai · {trader.mentor[1]}</div>
            </div>
            <span className="text-[10px] font-bold py-1 px-2 bg-[#EFF8F3] text-[#157A4C] !rounded whitespace-nowrap">
              ĐÃ XÁC MINH
            </span>
          </div>
          <div className="text-xs md:text-sm leading-relaxed text-[#3F4A5A] pl-3 border-l-[3px] border-[#E39A2B]">
            {trader.mentor[2]}
          </div>
          <div className="flex items-center gap-2.5 mt-3.5">
            <div className="w-7 h-7 !rounded-full bg-[#F1F4F8] text-[#5C6779] flex items-center justify-center text-[10.5px] font-bold">
              NTB
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-900">{trader.mentor[0]}</div>
              <div className="text-[11px] text-[#8B95A3]">Mentor · LUMIR</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
